import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  ArrowOutwardRounded as ArrowOutwardRoundedIcon,
  BugReportOutlined as BugReportOutlinedIcon,
  HubOutlined as HubOutlinedIcon,
  LinkOutlined as LinkOutlinedIcon,
  LockOpenOutlined as LockOpenOutlinedIcon,
  PublicOutlined as PublicOutlinedIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  StorageOutlined as StorageOutlinedIcon,
} from '@mui/icons-material';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const VERIFY_THRESHOLD = 25;
const METHOD_ORDER = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const METHOD_COLORS = {
  GET: '#8ec5ff',
  POST: '#9dd7b0',
  PUT: '#ffd98a',
  DELETE: '#ffb59f',
  PATCH: '#d8b4f8',
};

const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const gradePalette = {
  'A+': '#2e7d32',
  'A-': '#2e7d32',
  B: '#1565c0',
  C: '#ef6c00',
  D: '#ed6c02',
  F: '#d32f2f',
  '?': '#90a4ae',
};

const severityMeta = {
  critical: { label: 'Critical', color: '#7b1fa2', weight: 6, order: 5 },
  high: { label: 'High', color: '#d32f2f', weight: 4, order: 4 },
  medium: { label: 'Medium', color: '#ef6c00', weight: 3, order: 3 },
  low: { label: 'Low', color: '#1976d2', weight: 2, order: 2 },
  info: { label: 'Info', color: '#2e7d32', weight: 1, order: 1 },
};

const toneMap = {
  blue: { color: PRIMARYCOLOR, bg: alpha(PRIMARYCOLOR, 0.08) },
  red: { color: '#d32f2f', bg: alpha('#d32f2f', 0.08) },
  amber: { color: '#ef6c00', bg: alpha('#ef6c00', 0.08) },
  green: { color: '#2e7d32', bg: alpha('#2e7d32', 0.08) },
  slate: { color: '#455a64', bg: alpha('#455a64', 0.08) },
  purple: { color: '#7b1fa2', bg: alpha('#7b1fa2', 0.08) },
};

const cardShellSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.09),
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.06)',
  bgcolor: '#fff',
};

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const severityKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('critical')) return 'critical';
  if (normalized.includes('high')) return 'high';
  if (normalized.includes('medium')) return 'medium';
  if (normalized.includes('low')) return 'low';
  return 'info';
};

const normalizeMethod = (value, fallback = '') => {
  const normalized = String(value || fallback || '').trim().toUpperCase();
  return normalized || fallback;
};

const normalizePath = (value, fallback = 'Unknown endpoint') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const buildEndpointKey = (path, method = '') => {
  const normalizedPath = normalizePath(path);
  const normalizedMethod = normalizeMethod(method);
  return normalizedMethod ? `${normalizedMethod} ${normalizedPath}` : normalizedPath;
};

const getAttackFindings = (attack) =>
  Array.isArray(attack?.exploit_results?.findings) ? attack.exploit_results.findings : [];

const normalizeScopeShape = (raw) => {
  let scope = raw;

  try {
    if (typeof scope === 'string') scope = JSON.parse(scope);
  } catch {
    // ignore parse errors
  }

  if (scope && typeof scope === 'object') {
    if ('scope' in scope && scope.scope && typeof scope.scope === 'object') scope = scope.scope;
    if (!scope.login_url && !scope.credentials && scope.data && typeof scope.data === 'object') scope = scope.data;
  }

  return scope && typeof scope === 'object' ? scope : null;
};

const parseLocation = (value) => {
  if (!value) return { path: '', method: '' };

  if (Array.isArray(value)) {
    return parseLocation(value.find(Boolean) || '');
  }

  const raw = String(value).trim();
  if (!raw) return { path: '', method: '' };

  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parseLocation(parsed.find(Boolean) || '');
    } catch {
      // ignore parse errors
    }
  }

  const parts = raw.split(' - ');
  if (parts.length >= 2) {
    const method = normalizeMethod(parts[parts.length - 1]);
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(parts.slice(0, -1).join(' - ')),
        method,
      };
    }
  }

  const matched = raw.match(/^(.*)\s+-\s+([A-Z]+)$/i);
  if (matched) {
    const method = normalizeMethod(matched[2]);
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(matched[1]),
        method,
      };
    }
  }

  return { path: normalizePath(raw), method: '' };
};

const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Card sx={{ ...cardShellSx, p: 3, ...sx }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Card>
);

const MetricCard = ({ label, value, caption, icon: Icon, tone = 'blue' }) => {
  const toneStyle = toneMap[tone] || toneMap.blue;

  return (
    <Card sx={{ ...cardShellSx, p: 2.2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: toneStyle.bg,
            color: toneStyle.color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1, color: '#10243E', mt: 0.35 }}>
            {value}
          </Typography>
          {caption ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7, lineHeight: 1.45 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
};

const SeverityCountPills = ({ counts, prefix }) => (
  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
    {['critical', 'high', 'medium', 'low', 'info'].map((key) => (
      <Chip
        key={`${prefix}-${key}`}
        size="small"
        label={`${severityMeta[key].label} ${num(counts?.[key], 0)}`}
        sx={{
          height: 26,
          bgcolor: alpha(severityMeta[key].color, 0.1),
          color: severityMeta[key].color,
          fontWeight: 800,
        }}
      />
    ))}
  </Stack>
);

const BarList = ({ rows, color, emptyLabel, secondaryKey = 'value' }) => {
  if (!rows.length) {
    return <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>;
  }

  const maxValue = Math.max(1, ...rows.map((row) => num(row.value, 0)));

  return (
    <Stack spacing={1.35}>
      {rows.map((row) => {
        const percent = (num(row.value, 0) / maxValue) * 100;
        return (
          <Box key={row.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#10243E', minWidth: 0 }} noWrap title={row.label}>
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {row[secondaryKey]}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                mt: 0.7,
                height: 8,
                borderRadius: 999,
                bgcolor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: color,
                },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const FlowStat = ({ label, value, helper, tone }) => {
  const toneStyle = toneMap[tone] || toneMap.blue;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: toneStyle.bg,
        border: '1px solid',
        borderColor: alpha(toneStyle.color, 0.14),
        height: '100%',
      }}
    >
      <Typography variant="caption" sx={{ color: toneStyle.color, fontWeight: 800, letterSpacing: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 950, color: '#10243E', mt: 0.35 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7, lineHeight: 1.45 }}>
        {helper}
      </Typography>
    </Box>
  );
};

const SeverityMethodCard = ({
  severityCounts,
  methodStats,
  activeMethodCount,
  totalMethodObservations,
  stateChangingObservations,
  authenticatedScope,
}) => (
  <SectionCard title="Severity and Method Mix" subtitle="Validated findings and request-method distribution across the current web surface.">
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Validated findings
        </Typography>
        <SeverityCountPills counts={severityCounts} prefix="web-findings" />
      </Box>

      <Divider />

      <Grid container spacing={1.25}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(PRIMARYCOLOR, 0.06) }}>
            <Typography variant="caption" color="text.secondary">
              Observed methods
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
              {activeMethodCount}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Distinct HTTP verbs across inventoried endpoints.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha('#ef6c00', 0.06) }}>
            <Typography variant="caption" color="text.secondary">
              State-changing routes
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
              {stateChangingObservations}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              POST, PUT, PATCH, and DELETE observations.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(authenticatedScope ? '#2e7d32' : '#455a64', 0.06) }}>
            <Typography variant="caption" color="text.secondary">
              Authentication context
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
              {authenticatedScope ? 'Present' : 'Limited'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {authenticatedScope ? 'Login URL or credentials were supplied.' : 'Dashboard is currently reading unauthenticated scope only.'}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Divider />

      <Stack spacing={1.1}>
        {methodStats.map((item) => (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.7 }}>
              <Chip
                size="small"
                label={item.label}
                sx={{
                  bgcolor: METHOD_COLORS[item.label] || alpha(PRIMARYCOLOR, 0.12),
                  color: '#10243E',
                  fontWeight: 800,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.value} observations
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={totalMethodObservations ? (item.value / totalMethodObservations) * 100 : 0}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: alpha(METHOD_COLORS[item.label] || PRIMARYCOLOR, 0.12),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: METHOD_COLORS[item.label] || PRIMARYCOLOR,
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  </SectionCard>
);

const PentestWebDashboard = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [endpointsData, setEndpointsData] = useState([]);
  const [exploitsData, setExploitsData] = useState([]);
  const [threatModel, setThreatModel] = useState(null);
  const [scopeRaw, setScopeRaw] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return undefined;
    }

    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, endpointsRes, exploitsRes, threatRes, scopeRes] = await Promise.all([
          fetch(`${BASEURL}/pentests/web/stats?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentests/web/endpoints?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentests/web/threat_model?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentest/scope?pentest_id=${pentest_id}`, { headers }),
        ]);

        const [statsJson, endpointsJson, exploitsJson, threatJson, scopeJson] = await Promise.all([
          statsRes.json(),
          endpointsRes.json(),
          exploitsRes.json(),
          threatRes.json(),
          scopeRes.json(),
        ]);

        const invalidToken = [statsJson, endpointsJson, exploitsJson, threatJson, scopeJson].some(
          (payload) => payload?.message === 'Invalid token' || (payload?.error && payload?.message === 'Invalid token')
        );

        if (invalidToken) {
          navigate('/');
          return;
        }

        if (!active) return;

        setStats(statsJson?.stats || statsJson || null);
        setEndpointsData(Array.isArray(endpointsJson?.endpoints) ? endpointsJson.endpoints : []);
        setExploitsData(Array.isArray(exploitsJson?.exploits) ? exploitsJson.exploits : []);
        setThreatModel(threatJson?.success ? threatJson?.threat_model || null : null);
        setScopeRaw((scopeJson?.scope?.scope) ?? scopeJson?.scope ?? scopeJson?.data ?? scopeJson ?? null);
      } catch (fetchError) {
        console.error(fetchError);
        if (!active) return;
        setError('Unable to load the web application dashboard right now.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [navigate, pentest_id]);

  const normalizedScope = useMemo(() => normalizeScopeShape(scopeRaw), [scopeRaw]);

  const scopeStats = useMemo(() => {
    const credentials = Array.isArray(normalizedScope?.credentials) ? normalizedScope.credentials : [];
    const uniqueUsers = new Set();

    credentials.forEach((item) => {
      const user = String(item?.username || item?.email || '').trim();
      if (user) uniqueUsers.add(user);
    });

    return {
      loginUrl: String(normalizedScope?.login_url || '').trim(),
      credentialCount: credentials.length,
      uniqueUserCount: uniqueUsers.size,
      authenticatedScope: Boolean(normalizedScope?.login_url) || uniqueUsers.size > 0,
    };
  }, [normalizedScope]);

  const endpointRows = useMemo(() => {
    const map = new Map();
    const pathIndex = new Map();

    const attachPathIndex = (path, key) => {
      const existing = pathIndex.get(path) || [];
      if (!existing.includes(key)) existing.push(key);
      pathIndex.set(path, existing);
    };

    const ensureRow = (path, method, seed = {}) => {
      const normalizedPath = normalizePath(path);
      const normalizedMethod = normalizeMethod(method, seed.method || '');
      const directKey = buildEndpointKey(normalizedPath, normalizedMethod);
      const pathCandidates = pathIndex.get(normalizedPath) || [];

      if (!normalizedMethod && pathCandidates.length === 1) {
        return map.get(pathCandidates[0]);
      }

      if (!map.has(directKey)) {
        map.set(directKey, {
          key: directKey,
          path: normalizedPath,
          method: normalizedMethod || 'GET',
          totalExploits: 0,
          totalFindings: 0,
          totalAttacks: 0,
          queuedCount: 0,
          likelyCount: 0,
          maxScore: 0,
          severityCounts: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
          findingFamilies: new Set(),
          ...seed,
        });
        attachPathIndex(normalizedPath, directKey);
      }

      return map.get(directKey);
    };

    endpointsData.forEach((endpoint, index) => {
      const path = normalizePath(endpoint?.path || endpoint?.domain || endpoint?.url || endpoint?.endpoint || `Endpoint ${index + 1}`);
      const method = normalizeMethod(endpoint?.request_type || endpoint?.http_method, 'GET');
      const row = ensureRow(path, method, { method });

      row.totalExploits = Math.max(row.totalExploits, num(endpoint?.total_exploits_found, 0));
      row.severityCounts.critical = Math.max(row.severityCounts.critical, num(endpoint?.critical_exploits_found, 0));
      row.severityCounts.high = Math.max(row.severityCounts.high, num(endpoint?.high_exploits_found, 0));
      row.severityCounts.medium = Math.max(row.severityCounts.medium, num(endpoint?.medium_exploits_found, 0));
      row.severityCounts.low = Math.max(row.severityCounts.low, num(endpoint?.low_exploits_found, 0));
      row.severityCounts.info = Math.max(row.severityCounts.info, num(endpoint?.info_exploits_found, 0));
    });

    exploitsData.forEach((finding, index) => {
      const parsed = parseLocation(finding?.impacted_assets || finding?.endpoint || finding?.asset || `Finding ${index + 1}`);
      const row = ensureRow(parsed.path || `Finding ${index + 1}`, parsed.method);
      const severity = severityKey(finding?.severity);
      row.totalFindings += 1;
      row.severityCounts[severity] += 1;
      row.findingFamilies.add(String(finding?.name || `Finding ${index + 1}`).trim());
    });

    const modeledEndpoints = Array.isArray(threatModel?.endpoints) ? threatModel.endpoints : [];
    modeledEndpoints.forEach((endpoint, index) => {
      const path = normalizePath(endpoint?.path || `Modeled endpoint ${index + 1}`);
      const method = normalizeMethod(endpoint?.http_method, 'GET');
      const row = ensureRow(path, method, { method });
      const attacks = Array.isArray(endpoint?.threat_model?.attacks)
        ? endpoint.threat_model.attacks
        : Array.isArray(endpoint?.attacks)
          ? endpoint.attacks
          : [];

      attacks.forEach((attack) => {
        const attackScore = num(attack?.score, 0);
        row.totalAttacks += 1;
        if (attackScore >= VERIFY_THRESHOLD) row.queuedCount += 1;
        if (attackScore >= 75) row.likelyCount += 1;
        row.maxScore = Math.max(row.maxScore, attackScore);

        getAttackFindings(attack).forEach((finding) => {
          const severity = severityKey(finding?.severity);
          row.severityCounts[severity] += 1;
          row.totalFindings += 1;
        });
      });
    });

    return [...map.values()]
      .map((row) => {
        const highestSeverity = ['critical', 'high', 'medium', 'low', 'info'].find((key) => num(row.severityCounts[key], 0) > 0) || 'info';
        const exposureWeight =
          row.maxScore +
          row.likelyCount * 18 +
          row.queuedCount * 8 +
          Object.entries(row.severityCounts).reduce(
            (sum, [key, value]) => sum + severityMeta[key].weight * num(value, 0) * 6,
            0
          ) +
          row.totalFindings * 4 +
          row.totalExploits * 3;

        return {
          ...row,
          highestSeverity,
          exposureWeight,
        };
      })
      .sort(
        (left, right) =>
          right.exposureWeight - left.exposureWeight ||
          right.totalFindings - left.totalFindings ||
          right.maxScore - left.maxScore ||
          left.path.localeCompare(right.path)
      );
  }, [endpointsData, exploitsData, threatModel]);

  const findingSeverity = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    exploitsData.forEach((finding) => {
      counts[severityKey(finding?.severity)] += 1;
    });
    return counts;
  }, [exploitsData]);

  const methodCounts = useMemo(() => {
    const statsCounts = {
      GET: num(stats?.total_get_methods, 0),
      POST: num(stats?.total_post_methods, 0),
      PUT: num(stats?.total_put_methods, 0),
      DELETE: num(stats?.total_delete_methods, 0),
      PATCH: num(stats?.total_patch_methods, 0),
    };
    const statsTotal = METHOD_ORDER.reduce((sum, method) => sum + statsCounts[method], 0);

    if (statsTotal > 0) return statsCounts;

    return endpointRows.reduce(
      (acc, row) => ({
        ...acc,
        [row.method]: (acc[row.method] || 0) + 1,
      }),
      { GET: 0, POST: 0, PUT: 0, DELETE: 0, PATCH: 0 }
    );
  }, [endpointRows, stats]);

  const methodStats = useMemo(
    () => METHOD_ORDER.map((label) => ({ label, value: num(methodCounts[label], 0) })),
    [methodCounts]
  );

  const totalMethodObservations = useMemo(
    () => methodStats.reduce((sum, item) => sum + item.value, 0),
    [methodStats]
  );

  const activeMethodCount = useMemo(
    () => methodStats.filter((item) => item.value > 0).length,
    [methodStats]
  );

  const stateChangingObservations = useMemo(
    () => ['POST', 'PUT', 'PATCH', 'DELETE'].reduce((sum, method) => sum + num(methodCounts[method], 0), 0),
    [methodCounts]
  );

  const threatOverview = useMemo(() => ({
    totalEndpoints: Array.isArray(threatModel?.endpoints) ? threatModel.endpoints.length : 0,
    totalAttacks: endpointRows.reduce((sum, row) => sum + row.totalAttacks, 0),
    totalQueued: endpointRows.reduce((sum, row) => sum + row.queuedCount, 0),
    likelyPaths: endpointRows.reduce((sum, row) => sum + row.likelyCount, 0),
    endpointsWithLikelyPaths: endpointRows.filter((row) => row.likelyCount > 0).length,
    endpointsWithFindings: endpointRows.filter((row) => row.totalFindings > 0).length,
    topEndpointsByRisk: endpointRows.slice(0, 6),
  }), [endpointRows, threatModel]);

  const endpointCoverageRows = useMemo(
    () =>
      endpointRows
        .slice()
        .sort((left, right) => right.totalExploits + right.totalAttacks - (left.totalExploits + left.totalAttacks))
        .slice(0, 5)
        .map((row) => ({
          label: `${row.method} ${row.path}`,
          value: row.totalExploits + row.totalAttacks,
          secondary: `${row.totalFindings} findings`,
        })),
    [endpointRows]
  );

  const findingsSummary = useMemo(() => {
    const map = new Map();

    exploitsData.forEach((finding, index) => {
      const label = String(finding?.name || `Finding ${index + 1}`).trim();
      const severity = severityKey(finding?.severity);
      const parsed = parseLocation(finding?.impacted_assets || finding?.endpoint || finding?.asset || '');
      const endpointLabel = parsed.path ? `${parsed.method ? `${parsed.method} ` : ''}${parsed.path}` : 'Unknown endpoint';
      const current = map.get(label) || {
        label,
        value: 0,
        endpointCount: new Set(),
        highestSeverity: 'info',
      };

      current.value += 1;
      current.endpointCount.add(endpointLabel);
      if (severityMeta[severity].order > severityMeta[current.highestSeverity].order) {
        current.highestSeverity = severity;
      }

      map.set(label, current);
    });

    return [...map.values()]
      .sort(
        (left, right) =>
          severityMeta[right.highestSeverity].order - severityMeta[left.highestSeverity].order ||
          right.value - left.value ||
          left.label.localeCompare(right.label)
      )
      .slice(0, 6)
      .map((item) => ({
        label: item.label,
        value: item.value,
        highestSeverity: item.highestSeverity,
        endpointCount: item.endpointCount.size,
      }));
  }, [exploitsData]);

  const topImpactedEndpointRows = useMemo(
    () =>
      endpointRows
        .filter((row) => row.totalFindings > 0)
        .slice(0, 5)
        .map((row) => ({
          label: `${row.method} ${row.path}`,
          value: row.totalFindings,
          secondary: `${severityMeta[row.highestSeverity].label} - ${row.totalFindings} finding${row.totalFindings === 1 ? '' : 's'}`,
        })),
    [endpointRows]
  );

  const riskScore = Number.isFinite(Number(stats?.risk_score)) ? Number(stats?.risk_score) : null;
  const grade = getGrade(riskScore);
  const gradeColor = gradePalette[grade] || gradePalette['?'];

  const riskNarrative = useMemo(() => {
    const criticalHigh = num(findingSeverity.critical, 0) + num(findingSeverity.high, 0);

    if (criticalHigh > 0) {
      return `${criticalHigh} critical or high-severity findings are concentrated across ${topImpactedEndpointRows.length || endpointRows.filter((row) => row.totalFindings > 0).length} web endpoints. Prioritize authenticated and state-changing routes with repeated exposure first.`;
    }

    if (threatOverview.likelyPaths > 0) {
      return `${threatOverview.likelyPaths} likely attack paths are modeled across ${threatOverview.totalEndpoints} endpoints. Focus review on routes with elevated scores and queued validation work.`;
    }

    if (endpointRows.length > 0) {
      return `${endpointRows.length} endpoints are currently inventoried across ${activeMethodCount} observed HTTP methods. Continue validating the highest-risk routes and authenticated workflows as coverage expands.`;
    }

    return 'Assessment telemetry is still populating. Endpoint inventory, attack modeling, and findings will appear here as the engagement progresses.';
  }, [activeMethodCount, endpointRows, findingSeverity.critical, findingSeverity.high, threatOverview, topImpactedEndpointRows.length]);

  const needsAttention = num(findingSeverity.critical, 0) + num(findingSeverity.high, 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading web application dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
        <Nav />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!stats && !endpointRows.length && !exploitsData.length) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
        <Nav />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            No dashboard data is available for this web assessment yet.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
      <Nav />
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
        <Stack spacing={2.5}>
          <SectionCard
            title="Web Application Dashboard"
            subtitle="Assessment coverage, attack modeling, and validated exposure across the inventoried application routes."
            action={
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Button size="small" variant="outlined" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/scope/${pentest_id}`)} sx={{ borderRadius: 999 }}>
                  Scope
                </Button>
                <Button size="small" variant="outlined" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/assets/${pentest_id}`)} sx={{ borderRadius: 999 }}>
                  Endpoints
                </Button>
                <Button size="small" variant="outlined" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)} sx={{ borderRadius: 999 }}>
                  Threat Model
                </Button>
                <Button size="small" variant="outlined" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/exploits/${pentest_id}`)} sx={{ borderRadius: 999 }}>
                  Findings
                </Button>
              </Stack>
            }
            sx={{ p: 2.6 }}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Chip
                label={`${num(stats?.total_endpoints, endpointRows.length)} inventoried endpoint${num(stats?.total_endpoints, endpointRows.length) === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }}
              />
              <Chip label={`${activeMethodCount} active HTTP method${activeMethodCount === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
              <Chip label={`${threatOverview.totalAttacks} modeled attack path${threatOverview.totalAttacks === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
              <Chip label={`${exploitsData.length} validated finding${exploitsData.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
              <Chip
                label={scopeStats.authenticatedScope ? 'Authenticated scope provided' : 'Unauthenticated scope only'}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(scopeStats.authenticatedScope ? '#2e7d32' : '#455a64', 0.08),
                  color: scopeStats.authenticatedScope ? '#2e7d32' : '#455a64',
                }}
              />
            </Stack>
          </SectionCard>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card sx={{ ...cardShellSx, p: 3, height: '100%' }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: 3,
                        bgcolor: alpha(gradeColor, 0.1),
                        color: gradeColor,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShieldOutlinedIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Risk Posture
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.05 }}>
                        Grade {grade}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
                    <Typography variant="h1" sx={{ fontWeight: 950, color: gradeColor, lineHeight: 0.9 }}>
                      {grade}
                    </Typography>
                    <Box sx={{ pb: 0.6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Assessment score
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {riskScore == null ? 'N/A' : `${Math.round(riskScore)} / 100`}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {riskNarrative}
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Validated Findings"
                        value={exploitsData.length}
                        caption="Confirmed issues currently recorded against the application surface."
                        icon={BugReportOutlinedIcon}
                        tone={needsAttention > 0 ? 'red' : 'blue'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Likely Attack Paths"
                        value={threatOverview.likelyPaths}
                        caption="High-confidence model outputs that deserve immediate analyst review."
                        icon={AccountTreeOutlinedIcon}
                        tone={threatOverview.likelyPaths > 0 ? 'amber' : 'blue'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Endpoints in Scope"
                        value={num(stats?.total_endpoints, endpointRows.length)}
                        caption="Reachable pages, routes, and endpoints currently inventoried."
                        icon={LinkOutlinedIcon}
                        tone="blue"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Observed Methods"
                        value={activeMethodCount}
                        caption="Distinct HTTP verbs currently represented across the application."
                        icon={PublicOutlinedIcon}
                        tone="green"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Credentials Provided"
                        value={scopeStats.credentialCount}
                        caption="Credential pairs or user records supplied for authenticated testing."
                        icon={StorageOutlinedIcon}
                        tone={scopeStats.credentialCount > 0 ? 'green' : 'slate'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        label="Login URL"
                        value={scopeStats.loginUrl ? 'Present' : 'Missing'}
                        caption={scopeStats.loginUrl ? 'Authenticated entry point is available for workflow testing.' : 'Dashboard is currently reading no login URL from scope.'}
                        icon={LockOpenOutlinedIcon}
                        tone={scopeStats.loginUrl ? 'green' : 'slate'}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <SeverityMethodCard
                severityCounts={findingSeverity}
                methodStats={methodStats}
                activeMethodCount={activeMethodCount}
                totalMethodObservations={totalMethodObservations}
                stateChangingObservations={stateChangingObservations}
                authenticatedScope={scopeStats.authenticatedScope}
              />
            </Grid>
          </Grid>

          <SectionCard
            title="Attack Verification Flow"
            subtitle="From inventoried routes through modeled attack paths and confirmed findings."
            action={
              <Button size="small" variant="text" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)}>
                Review threat model
              </Button>
            }
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} lg={3}>
                <FlowStat
                  label="Modeled Endpoints"
                  value={threatOverview.totalEndpoints}
                  helper="Routes currently represented inside the threat-model output."
                  tone="blue"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <FlowStat
                  label="Likely Endpoints"
                  value={threatOverview.endpointsWithLikelyPaths}
                  helper="Endpoints carrying at least one likely attack path."
                  tone="amber"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <FlowStat
                  label="Queued Reviews"
                  value={threatOverview.totalQueued}
                  helper={`Attack paths scoring ${VERIFY_THRESHOLD}+ and ready for analyst validation.`}
                  tone="purple"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <FlowStat
                  label="Confirmed Findings"
                  value={exploitsData.length}
                  helper="Validated issues currently recorded against application routes."
                  tone={needsAttention > 0 ? 'red' : 'green'}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={1.1}>
                  {[
                    {
                      label: 'Below verify threshold',
                      value: Math.max(0, threatOverview.totalAttacks - threatOverview.totalQueued),
                      color: '#90a4ae',
                    },
                    {
                      label: 'Queued for validation',
                      value: Math.max(0, threatOverview.totalQueued - threatOverview.likelyPaths),
                      color: PRIMARYCOLOR,
                    },
                    {
                      label: 'Likely attack paths',
                      value: threatOverview.likelyPaths,
                      color: '#d32f2f',
                    },
                  ].map((row) => {
                    const total = Math.max(1, threatOverview.totalAttacks);
                    return (
                      <Box key={row.label}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.7 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E' }}>
                            {row.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.value}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={(row.value / total) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            bgcolor: alpha(row.color, 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 999,
                              bgcolor: row.color,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(PRIMARYCOLOR, 0.04),
                    border: '1px solid',
                    borderColor: alpha(PRIMARYCOLOR, 0.08),
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                    Current Assessment Read
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Threat modeling is currently populated across {threatOverview.totalEndpoints} endpoints with {threatOverview.totalAttacks} total attack paths under review.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {threatOverview.endpointsWithFindings} endpoints already have confirmed findings and {threatOverview.endpointsWithLikelyPaths} show likely attack-path pressure.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {scopeStats.authenticatedScope
                        ? 'Authenticated scope is available, which means higher-value workflow and authorization testing should stay prioritized.'
                        : 'No authenticated scope is currently visible here, so route-level and unauthenticated exposure will dominate the early posture.'}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </SectionCard>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Top At-Risk Endpoints"
                subtitle="Endpoints with the strongest combination of validated findings, modeled attack pressure, and repeated route exposure."
                action={
                  <Button size="small" variant="text" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/exploits/${pentest_id}`)}>
                    Open findings
                  </Button>
                }
              >
                {endpointRows.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Endpoint</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Method</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Risk Signal</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Findings</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Attack Paths</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {endpointRows.slice(0, 7).map((row) => {
                          const maxExposure = Math.max(1, ...endpointRows.slice(0, 7).map((entry) => entry.exposureWeight));
                          const signalColor = row.maxScore >= 75 ? '#d32f2f' : row.maxScore >= 50 ? '#ef6c00' : PRIMARYCOLOR;
                          return (
                            <TableRow key={row.key} hover>
                              <TableCell sx={{ maxWidth: 260 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#10243E' }} noWrap title={row.path}>
                                  {row.path}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.findingFamilies.size || 0} finding families
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.method}
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: METHOD_COLORS[row.method] || alpha(PRIMARYCOLOR, 0.1),
                                    color: '#10243E',
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 180 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(row.exposureWeight / maxExposure) * 100}
                                  sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: alpha(signalColor, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 999,
                                      bgcolor: signalColor,
                                    },
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
                                  Max score {Math.round(row.maxScore)} - exposure index {Math.round(row.exposureWeight)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.totalFindings}
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.1),
                                    color: severityMeta[row.highestSeverity].color,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {row.totalAttacks} total - {row.likelyCount} likely
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Endpoints will rank here once route inventory, threat modeling, and findings data are available.
                  </Typography>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Likely Attack Paths"
                subtitle="Highest-scoring web routes from the threat model, ranked by confidence and observed findings."
                action={
                  <Button size="small" variant="text" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)}>
                    Review endpoints
                  </Button>
                }
              >
                {threatOverview.topEndpointsByRisk.length ? (
                  <Stack spacing={1.25}>
                    {threatOverview.topEndpointsByRisk.map((row) => {
                      const scoreColor = row.maxScore >= 75 ? '#d32f2f' : row.maxScore >= 50 ? '#ef6c00' : PRIMARYCOLOR;
                      return (
                        <Box
                          key={row.key}
                          sx={{
                            p: 1.8,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: alpha(scoreColor, 0.12),
                            bgcolor: alpha(scoreColor, 0.03),
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.8 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E' }} noWrap title={row.path}>
                                {row.path}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.method} route
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={`Score ${Math.round(row.maxScore)}`}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(scoreColor, 0.12),
                                color: scoreColor,
                              }}
                            />
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, row.maxScore)}
                            sx={{
                              height: 8,
                              borderRadius: 999,
                              bgcolor: alpha(scoreColor, 0.08),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                bgcolor: scoreColor,
                              },
                            }}
                          />

                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                            <Chip size="small" label={`${row.totalAttacks} paths`} />
                            <Chip size="small" label={`${row.queuedCount} queued`} />
                            <Chip size="small" label={`${row.totalFindings} findings`} />
                            <Chip size="small" label={`${row.totalExploits} exploit observations`} />
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Threat-model routes will appear here as soon as attack paths are generated for the application.
                  </Typography>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Endpoint Coverage"
                subtitle="Method coverage, authenticated context, and the routes currently receiving the most testing pressure."
                action={
                  <Button size="small" variant="text" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/assets/${pentest_id}`)}>
                    Review endpoints
                  </Button>
                }
              >
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(PRIMARYCOLOR, 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Endpoints
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {num(stats?.total_endpoints, endpointRows.length)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha('#2e7d32', 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Active Methods
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {activeMethodCount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(scopeStats.loginUrl ? '#2e7d32' : '#455a64', 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Login URL
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {scopeStats.loginUrl ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha('#ef6c00', 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Unique Users
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {scopeStats.uniqueUserCount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      HTTP Method Distribution
                    </Typography>
                    <BarList
                      rows={methodStats
                        .filter((row) => row.value > 0)
                        .map((row) => ({ label: row.label, value: row.value, secondary: `${row.value} observed` }))}
                      color={PRIMARYCOLOR}
                      emptyLabel="No request-method observations are available yet."
                      secondaryKey="secondary"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      Most Active Routes
                    </Typography>
                    <BarList
                      rows={endpointCoverageRows}
                      color="#2e7d32"
                      emptyLabel="Routes will populate here as endpoint inventory and findings expand."
                      secondaryKey="secondary"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Alert
                  icon={<LockOpenOutlinedIcon fontSize="inherit" />}
                  sx={{
                    borderRadius: 3,
                    bgcolor: alpha(scopeStats.authenticatedScope ? '#2e7d32' : PRIMARYCOLOR, 0.05),
                    color: '#23425d',
                    border: '1px solid',
                    borderColor: alpha(scopeStats.authenticatedScope ? '#2e7d32' : PRIMARYCOLOR, 0.1),
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {scopeStats.loginUrl
                      ? `Login entry point in scope: ${scopeStats.loginUrl}${scopeStats.credentialCount ? ` with ${scopeStats.credentialCount} credential record${scopeStats.credentialCount === 1 ? '' : 's'}` : ''}.`
                      : 'No login URL is currently visible from scope. As scope evolves, authenticated route context will appear here automatically.'}
                  </Typography>
                </Alert>
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Findings Spotlight"
                subtitle="Repeated finding families and the endpoints absorbing the most validated exposure."
                action={
                  <Button size="small" variant="text" endIcon={<ArrowOutwardRoundedIcon />} onClick={() => navigate(`/pentest/web/exploits/${pentest_id}`)}>
                    Investigate findings
                  </Button>
                }
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                  Most Repeated Finding Families
                </Typography>

                {findingsSummary.length ? (
                  <Stack spacing={1.2}>
                    {findingsSummary.map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          p: 1.7,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: alpha(severityMeta[row.highestSeverity].color, 0.12),
                          bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.035),
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E', minWidth: 0 }} noWrap title={row.label}>
                            {row.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${row.value}x`}
                            sx={{
                              fontWeight: 800,
                              bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.12),
                              color: severityMeta[row.highestSeverity].color,
                            }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                          <Chip size="small" label={severityMeta[row.highestSeverity].label} />
                          <Chip size="small" label={`${row.endpointCount} endpoint${row.endpointCount === 1 ? '' : 's'}`} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Finding families will appear here once validated issues are available for the application.
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                  Blast Radius
                </Typography>
                <BarList
                  rows={topImpactedEndpointRows}
                  color="#d32f2f"
                  emptyLabel="Impacted routes will populate here as findings continue to accumulate."
                  secondaryKey="secondary"
                />
              </SectionCard>
            </Grid>
          </Grid>

          {needsAttention > 0 ? (
            <Alert
              icon={<HubOutlinedIcon fontSize="inherit" />}
              sx={{
                borderRadius: 3,
                bgcolor: alpha('#d32f2f', 0.05),
                color: '#7a1f1f',
                border: '1px solid',
                borderColor: alpha('#d32f2f', 0.12),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Critical and high-severity issues are concentrated across {topImpactedEndpointRows.length || endpointRows.filter((row) => row.totalFindings > 0).length} endpoint{(topImpactedEndpointRows.length || endpointRows.filter((row) => row.totalFindings > 0).length) === 1 ? '' : 's'}.
                Start with the top-risk routes, likely attack paths, and state-changing methods to reduce the largest immediate exposure.
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

export default PentestWebDashboard;
